import React, { useRef, useCallback, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Link, Outlet } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { Layout as AntdLayout, Dropdown, Tooltip } from 'antd';
import ContainerSider from '@components/ContainerSider';
import useStores from '@lib/hooks/useStores';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { scMixinAbsoluteFull } from '@utils/emotion/mixins';
import ThemedLogo from '../UIKit/ThemedLogo';
import { ReactComponent as IconCoord } from '@svg/icon-editor-coord.svg';
import { ReactComponent as IconUpload } from '@svg/icon-upload-basic.svg';
import { ReactComponent as IconSuccess } from '@svg/common/icon-done-white.svg';
import { ReactComponent as IconFailed } from '@svg/common/icon-close.svg';
import { ReactComponent as IconEdit } from '@svg/icon-edit.svg';
import { ReactComponent as IconPaperAirplane } from '@svg/icon-paper-airplane.svg';
import UserModal from '@components/UserModal';
import ProcessingVideosDropdown from '@components/ProcessingVideosDropdown';
import { ReactComponent as IconPrefDropdownArrow } from '@svg/common/icon-pref-dropdown-arrow.svg';

import { IUserStore } from '@stores/userStore';
import { useAuthStore } from '@stores/auth';
import { useUIStore } from '@stores/ui';
import { useTranslation } from 'react-i18next';
import UploadingVideosDropdown from '../UploadingVideosDropdown';
import { getViewerUrlByStage } from '~/utils';
import ThemedSparkyLogo from '@components/UIKit/ThemedSparkyLogo';
import VideoSider from '@components/VideoSider';
import useVideoContext from '@lib/hooks/useVideoContext';

const { Header: AntdHeader } = AntdLayout;

export type ContainerType = 'default' | 'video' | 'sidebarless';

interface IContainerProps {
  type: ContainerType;
  headerProfile?: boolean;
}

const Container: React.FC<IContainerProps> = observer(
  ({ type = 'default', headerProfile = false }) => {
    const { t } = useTranslation();
    const { userStore } = useStores<{
      userStore: IUserStore;
    }>();
    const { userData } = useAuthStore();
    const { isUserModalVisible, showUserModal } = useUIStore();
    const [{ data: videoData }] = useVideoContext();
    const userPictureRef = useRef<HTMLImageElement | null>(null);
    const [isDropdownExpanded, setDropdownExpanded] = useState(false);

    const getFeedbackMailtoUrlWithPlaceholder = useCallback(() => {
      const subject = t('header.opinion.placeholder.subject');
      const body = t('header.opinion.placeholder.body');
      const encodedSubject = encodeURIComponent(subject);
      const encodedBody = encodeURIComponent(body);

      return `mailto:info@devunlimit.com?subject=${encodedSubject}&body=${encodedBody}`;
    }, [t]);

    const onClickLogoDropdown = () => {
      setDropdownExpanded((prevState) => !prevState);
    };

    useEffect(() => {
      if (isDropdownExpanded) {
        document.body.addEventListener('click', blurHandler);
      }

      function blurHandler(e: any): void {
        const path = e.path || (e.composedPath && e.composedPath());
        if (path.some((element: HTMLElement) => element.id === 'logo_area')) return;
        if (isDropdownExpanded) setDropdownExpanded(false);
      }
      return () => {
        document.body.removeEventListener('click', blurHandler);
      };
    }, [isDropdownExpanded]);

    return (
      <Layout>
        <ContainerSider hidden={type === 'sidebarless'} />
        <CSSTransition
          in={type === 'video'}
          timeout={200}
          mountOnEnter
          unmountOnExit
          classNames="sider-transition"
        >
          <VideoSider data={videoData ?? undefined} />
        </CSSTransition>
        <Layout>
          <Header>
            <div id="logo_area" className="logo-area">
              <h1 className="logo">
                {type === 'sidebarless' ? (
                  <ThemedLogo />
                ) : (
                  <Link to="/" title="Home">
                    <span className="sr-only">Sparky.tv</span>
                    <ThemedLogo />
                  </Link>
                )}
              </h1>
              <button
                onClick={onClickLogoDropdown}
                aria-controls="logo_dropdown"
                aria-expanded={isDropdownExpanded ? 'true' : 'false'}
                className="logo-dropdown-expander"
              >
                <span className="icon">
                  <IconPrefDropdownArrow />
                </span>
              </button>
              <div id="logo_dropdown" className="logo-dropdown">
                <Tooltip title={t('common.button.sparky')} placement="bottom">
                  <div className="logo-dropdown-inner">
                    <a href={getViewerUrlByStage()} target="_blank" rel="noopener noreferrer">
                      <ThemedSparkyLogo />
                    </a>
                  </div>
                </Tooltip>
              </div>
            </div>
            <div className="action-area">
              <CSSTransition
                in={userStore.currentlyProcessingVideoIds.length > 0}
                timeout={300}
                mountOnEnter
                unmountOnExit
                classNames="header-progress-item-transition"
              >
                <ActionAreaItem>
                  <Dropdown overlay={<ProcessingVideosDropdown />} placement="bottomRight">
                    <div
                      className="inner"
                      role="button"
                      tabIndex={0}
                      aria-label={
                        userStore.isPoseProcessing
                          ? t('header.action.progress.title', {
                              count: userStore.currentProcessingVideoCount,
                            })
                          : t('header.action.progress.title.complete')
                      }
                    >
                      <i className="icon">
                        <IconCoord width="14" height="14" />
                      </i>
                      <div className="animated-icon-container">
                        <i className="animated-icon icon--estimate" />
                      </div>
                      <span
                        className="badge"
                        data-success={
                          !userStore.currentProcessingVideoCount &&
                          userStore.completedProcessingVideoIds.length > 0
                        }
                      >
                        {!userStore.currentProcessingVideoCount &&
                        userStore.completedProcessingVideoIds.length ? (
                          <span className="status-icon">
                            <IconSuccess width="12" height="9" />
                          </span>
                        ) : (
                          <i className="count">{userStore.currentProcessingVideoCount}</i>
                        )}
                      </span>
                    </div>
                  </Dropdown>
                </ActionAreaItem>
              </CSSTransition>
              <CSSTransition
                in={!!userStore.currentUploadingVideoCount}
                timeout={300}
                mountOnEnter
                unmountOnExit
                classNames="header-progress-item-transition"
              >
                <ActionAreaItem>
                  <Dropdown overlay={<UploadingVideosDropdown />} placement="bottomRight">
                    <div
                      className="inner"
                      role="button"
                      tabIndex={0}
                      aria-label={
                        userStore.isUploadingVideo
                          ? t('header.action.upload.title', {
                              count: userStore.currentUploadingVideoCount,
                            })
                          : t('header.action.upload.title.complete')
                      }
                    >
                      <i className="icon">
                        <IconUpload width="14" height="14" />
                      </i>
                      <div className="animated-icon-container">
                        <i className="animated-icon icon--upload" />
                      </div>
                      {(userStore.isUploadingVideo ||
                        userStore.currentUploadingVideoCount ||
                        userStore.hasErrorUploadingVideo) && (
                        <span
                          className="badge"
                          data-success={!userStore.isUploadingVideo}
                          data-error={userStore.hasErrorUploadingVideo}
                        >
                          {userStore.hasErrorUploadingVideo ? (
                            <span className="status-icon">
                              <IconFailed width="11" height="11" />
                            </span>
                          ) : userStore.isUploadingVideo ? (
                            <i className="count">{userStore.currentUploadingVideoCount}</i>
                          ) : (
                            <span className="status-icon">
                              <IconSuccess width="12" height="9" />
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </Dropdown>
                </ActionAreaItem>
              </CSSTransition>
              {headerProfile && userData && (
                <div className="user">
                  <a
                    href="#profile"
                    role="button"
                    className="thumbnail"
                    onClick={(e) => {
                      e.preventDefault();
                      showUserModal();
                    }}
                  >
                    <div className="frame">
                      <img
                        className="image"
                        src={userData.avatarUrl}
                        alt={userData.name}
                        ref={userPictureRef}
                        onError={() => {
                          userPictureRef.current!.src = '/assets/common/userpicture-default-sm.png';
                        }}
                      />
                    </div>
                    <div className="hover-dimmer">
                      <i className="icon">
                        <IconEdit width="20" height="20" />
                      </i>
                    </div>
                  </a>
                </div>
              )}
              <div className="direct-link">
                <a
                  href={getFeedbackMailtoUrlWithPlaceholder()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="icon">
                    <IconPaperAirplane />
                  </i>
                  <span className="text">{t('header.opinion.text')}</span>
                </a>
              </div>
            </div>
          </Header>
          <Outlet />
        </Layout>
        {isUserModalVisible && <UserModal />}
      </Layout>
    );
  },
);

const Layout = styled(AntdLayout)`
  overflow-y: auto;
  position: relative;
  z-index: 0;
  background-color: ${({ theme }) => theme.colors.primary.bgColor};

  article.ant-typography {
    margin-bottom: 16px;
  }

  h1.ant-typography {
    color: ${({ theme }) => theme.colors.headingColor};
  }

  .sub-sider {
    position: fixed;
  }

  .sider-transition {
  }
`;

const Header = styled(AntdHeader)`
  display: flex;
  height: 56px;
  padding: 0 36px;
  background-color: ${({ theme }) => theme.colors.solid.color};
  color: ${({ theme }) => theme.colors.fontColor};
  line-height: 1;

  .logo-area {
    position: relative;
    padding-right: 36px;

    .logo-dropdown-expander {
      display: block;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 32px;
      height: 100%;
      cursor: pointer;

      .icon {
        display: block;
        line-height: 0;

        path {
          ${({ theme }) =>
            theme.name === 'dark' &&
            `
              fill: #b6b7b8 !important;
          `}
        }

        svg {
          vertical-align: top;
        }
      }

      &[aria-expanded='true'] {
        .icon {
          transform: rotate(180deg);
        }

        & + .logo-dropdown {
          max-height: 56px;
        }
      }
    }

    .logo-dropdown {
      overflow: hidden;
      position: absolute;
      top: 100%;
      left: -36px;
      z-index: 1;
      max-height: 0;
      background-color: #fff;
      transition: max-height 200ms ease;
    }

    .logo-dropdown-inner {
      /* box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.1); */

      a {
        display: block;
        padding: 16px 56px 16px 36px;

        ${({ theme }) =>
          theme.name === 'dark' &&
          `
          background-color: #000;
        `}

        &:hover {
          background-color: #fbfbfb;

          ${({ theme }) =>
            theme.name === 'dark' &&
            `
            background-color: #262626;
          `}
        }

        svg {
          vertical-align: top;
        }
      }
    }
  }

  .logo {
    display: flex;
    align-items: center;
    height: 100%;
    margin: 0 auto 0 0;

    svg {
      display: block;
      width: auto;
      height: 24px;
    }
  }

  .action-area {
    display: flex;
    margin-left: auto;
  }

  .user {
    display: flex;
    align-items: center;
    height: 100%;
    margin-right: 32px;

    .thumbnail {
      overflow: hidden;
      position: relative;
      border-radius: 50%;
      outline: none;
      cursor: pointer;

      &::before {
        display: block;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border: 2px solid rgba(0, 0, 0, 0.05);
        border-radius: 50%;
        content: '';
      }

      &:hover,
      &:focus {
        .hover-dimmer {
          opacity: 1;
        }
      }

      .image {
        display: block;
        overflow: hidden;
        width: 32px;
        height: 32px;
        border-radius: 50%;

        &.is-empty {
          background-color: #eee;
        }
      }
    }

    .hover-dimmer {
      display: flex;
      justify-content: center;
      align-items: center;
      ${scMixinAbsoluteFull};
      background-color: rgba(9, 9, 9, 0.8);
      opacity: 0;
      transition: opacity 200ms ease;

      .icon {
        display: block;
        line-height: 1;

        svg path {
          fill: #fff;
        }
      }
    }
  }

  .direct-link {
    a {
      display: block;
      position: relative;
      margin: 14px 0 0;
      padding: 4px 18px 4px 36px;
      border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#5100ce' : '#b6b7b8')};
      border-radius: 3px;
    }

    .icon {
      display: block;
      position: absolute;
      top: 6px;
      left: 16px;

      svg {
        vertical-align: top;

        path {
          fill: ${({ theme }) => (theme.name === 'light' ? '#5100ce' : '#b6b7b8')};
        }
      }
    }

    .text {
      display: block;
      color: ${({ theme }) => (theme.name === 'light' ? '#5100ce' : '#b6b7b8')};
      font-size: 12px;
      line-height: 1.5;
    }
  }
`;

const EstimateSpriteAnimationKeyframe = keyframes`
  100% {
    background-position: 0 -1920px;
  }
`;

const UploadSpriteAnimationKeyframe = keyframes`
  100% {
    background-position: 0 -865px;
  }
`;

const ActionAreaItem = styled.div`
  margin: 0 18px 0 0;
  padding: 12px 0;

  .inner {
    position: relative;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #8100ff;
    text-align: center;
    line-height: 1;
    cursor: default;
    outline: none;

    &:hover {
      .icon {
        box-shadow: 0 0 4px #8100ff;
      }
    }

    .icon {
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      transition: all 200ms ease;

      svg {
        path {
          fill: #fff;
        }
      }
    }

    .animated-icon-container {
      display: block;
      overflow: hidden;
      position: absolute;
      top: 0;
      left: 0;
      width: 32px;
      height: 32px;
      z-index: 1;
      pointer-events: none;

      .animated-icon {
        display: block;
        width: 32px;
        height: 32px;
        background-repeat: no-repeat;
        background-position: top 0;

        &.icon--estimate {
          background-image: url('/assets/sprite/sprite-estimate-sequence@2x.png');
          background-size: 32px 1920px;
          animation: ${EstimateSpriteAnimationKeyframe} 2s steps(60) infinite;
        }

        &.icon--upload {
          background-image: url('/assets/sprite/sprite-upload-sequence@2x.png');
          background-size: 32px 865px;
          animation: ${UploadSpriteAnimationKeyframe} 1.5s steps(27) infinite;
        }
      }
    }

    .badge {
      position: absolute;
      top: -5px;
      right: -7px;
      z-index: 2;
      width: 19px;
      height: 19px;
      padding: 4px;
      border-radius: 50%;
      background-color: #f5222d;

      &[data-success='true'] {
        background-color: #52c41a;
      }

      &[data-error='true'] {
        background-color: #f5222d !important;
      }
    }

    .count {
      display: block;
      font-style: normal;
      font-size: 10px;
      line-height: 1;
      color: #fff;
    }

    .status-icon {
      position: relative;
      top: -2px;
    }
  }
`;

export default Container;
